import { useEffect, useState, useCallback, useRef } from 'react';
import { TransferItem } from 'antd/es/transfer';

import { useErrorHandler } from './useErrorHandler';

export interface TransferDataMapper<T> {
  key: (item: T) => string;
  title: (item: T) => string;
  description?: (item: T) => string;
  disabled?: (item: T) => boolean;
}

export interface UseTransferDataOptions<T> {
  fetchFn: () => Promise<T[]>;
  mapper: TransferDataMapper<T>;
  errorMessage?: string;
  fetchOnMount?: boolean;
}

export interface UseTransferDataReturn {
  items: TransferItem[];
  loading: boolean;
  refetch: () => Promise<void>;
}

export function useTransferData<T>({
  fetchFn,
  mapper,
  errorMessage = 'Erro ao buscar dados',
  fetchOnMount = true,
}: UseTransferDataOptions<T>): UseTransferDataReturn {
  const [items, setItems] = useState<TransferItem[]>([]);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  const { handleError } = useErrorHandler();

  const fetchFnRef = useRef(fetchFn);
  const mapperRef = useRef(mapper);
  const errorMessageRef = useRef(errorMessage);

  fetchFnRef.current = fetchFn;
  mapperRef.current = mapper;
  errorMessageRef.current = errorMessage;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchFnRef.current();

      const mappedItems: TransferItem[] = response.map((item) => {
        const mappedItem: TransferItem = {
          key: mapperRef.current.key(item),
          title: mapperRef.current.title(item),
          disabled: mapperRef.current.disabled?.(item) ?? false,
        };

        const description = mapperRef.current.description?.(item);
        if (description !== undefined) {
          mappedItem.description = description;
        }

        return mappedItem;
      });

      setItems(mappedItems);
    } catch (error) {
      handleError(error, errorMessageRef.current);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    if (fetchOnMount && !hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, [fetchOnMount, fetchData]);

  return {
    items,
    loading,
    refetch: fetchData,
  };
}
